// ─── Shared HRM data & helpers ──────────────────────────────────────────────
// Pulled out of Attendance.jsx so the same employee list / attendance log /
// status colors can be reused by the Staff Detail modal and the bulk
// Salary Slip screen without getting out of sync.

export const STATUS_OPTIONS = ["Present", "Absent", "Half-Day", "Leave"];

export const STATUS_COLORS = {
  Present: {
    light: "#31974D",
    dark: "#31974D",
    border: "#31974D",
    text: "#31974D",
  },
  Absent: {
    light: "#F93E3E",
    dark: "#F93E3E",
    border: "#F93E3E",
    text: "#F93E3E",
  },
  "Half-Day": {
    light: "#FF9B04",
    dark: "#FF9B04",
    border: "#FF9B04",
    text: "#FF9B04",
  },
  Leave: {
    light: "#e3d4fb",
    dark: "#31244A",
    border: "#8B5CF6",
    text: "#8B5CF6",
  },
  default: {
    light: "#ffffff",
    dark: "#1B1D21",
    border: "#ffffff",
    text: "#ffffff",
  },
};

export const TODAY = "2026-07-10";

// monthlySalary added so the Salary Slip screen has a real number to work
// with. joiningDate marks when the "Total Paid" history should start counting
// from. Replace both with whatever your Staff module already stores.
export const EMPLOYEES = [
  {
    id: 1,
    staffId: "STF001",
    name: "Rahul Sharma",
    department: "Production",
    shift: "Morning",
    monthlySalary: 24000,
    joiningDate: "2025-11-01",
  },
  {
    id: 2,
    staffId: "STF002",
    name: "Amit Singh",
    department: "Accounts",
    shift: "Morning",
    monthlySalary: 28000,
    joiningDate: "2025-08-16",
  },
  {
    id: 3,
    staffId: "STF003",
    name: "Neha Verma",
    department: "HR",
    shift: "General",
    monthlySalary: 26000,
    joiningDate: "2026-01-05",
  },
  {
    id: 4,
    staffId: "STF004",
    name: "Rohan Kumar",
    department: "Store",
    shift: "Evening",
    monthlySalary: 20000,
    joiningDate: "2025-05-20",
  },
  {
    id: 5,
    staffId: "STF005",
    name: "Priya Patel",
    department: "Sales",
    shift: "General",
    monthlySalary: 22000,
    joiningDate: "2026-03-10",
  },
  {
    id: 6,
    staffId: "STF006",
    name: "Ankit Gupta",
    department: "Dispatch",
    shift: "Morning",
    monthlySalary: 21000,
    joiningDate: "2025-09-01",
  },
];

export const DAILY_STATUS_BY_EMPLOYEE = {
  1: {
    "2026-07-06": "Present",
    "2026-07-07": "Present",
    "2026-07-08": "Present",
    "2026-07-09": "Half-Day",
    "2026-07-10": "Present",
  },
  2: {
    "2026-07-06": "Present",
    "2026-07-07": "Absent",
    "2026-07-08": "Present",
    "2026-07-09": "Present",
    "2026-07-10": "Present",
  },
  3: {
    "2026-07-06": "Leave",
    "2026-07-07": "Absent",
    "2026-07-08": "Present",
    "2026-07-09": "Present",
    "2026-07-10": "Absent",
  },
  4: {
    "2026-07-06": "Present",
    "2026-07-07": "Half-Day",
    "2026-07-08": "Half-Day",
    "2026-07-09": "Present",
    "2026-07-10": "Half-Day",
  },
  5: {
    "2026-07-06": "Leave",
    "2026-07-07": "Leave",
    "2026-07-08": "Present",
    "2026-07-09": "Present",
    "2026-07-10": "Leave",
  },
  6: {
    "2026-07-06": "Present",
    "2026-07-07": "Present",
    "2026-07-08": "Absent",
    "2026-07-09": "Present",
    "2026-07-10": "Present",
  },
};

export const EmployeeMap = Object.fromEntries(EMPLOYEES.map((e) => [e.id, e]));

export const buildInitialRecords = () => {
  const records = [];
  Object.entries(DAILY_STATUS_BY_EMPLOYEE).forEach(([employeeId, byDate]) => {
    Object.entries(byDate).forEach(([date, status]) => {
      records.push({
        id: `${employeeId}_${date}`,
        employeeId: Number(employeeId),
        date,
        status,
      });
    });
  });
  return records;
};

export const formatDate = (isoDate) =>
  new Date(isoDate).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export const formatDateTime = (isoString) =>
  new Date(isoString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const monthOptions = MONTH_NAMES.map((month, index) => ({
  label: month,
  value: index,
}));
const pad2 = (n) => String(n).padStart(2, "0");

/** Build a padded 6/7-row calendar grid for a given year+month (0-indexed month) */
export const buildMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = firstDay.getDay(); // 0 = Sunday

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(`${year}-${pad2(month + 1)}-${pad2(day)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/**
 * Attendance summary + salary breakdown for one employee, for one
 * year/month, given the flat `records` array (employeeId, date, status).
 */
/** Inclusive list of {year, month} pairs from a start date to an end date. */
export const getMonthsBetween = (startYear, startMonth, endYear, endMonth) => {
  const months = [];
  let y = startYear;
  let m = startMonth;
  // Guard against a bad range (e.g. joining date after the selected month).
  while (y < endYear || (y === endYear && m <= endMonth)) {
    months.push({ year: y, month: m });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return months;
};

/** Years to offer in a year picker: joining year through the current year (min 1 year span). */
export const getYearOptions = (joiningDate) => {
  const joinYear = new Date(joiningDate).getFullYear();
  const currentYear = new Date().getFullYear();
  const start = Math.min(joinYear, currentYear);
  const end = Math.max(joinYear, currentYear);
  const years = [];
  for (let y = start; y <= end; y++) years.push(y);
  return years;
};

/**
 * Every month's salary slip from the employee's joining date up through
 * (and including) uptoYear/uptoMonth, plus a grand total of what's been
 * paid across that whole span — nothing skipped, nothing double-counted.
 */
export const buildPaidHistory = (employee, records, uptoYear, uptoMonth) => {
  const join = new Date(employee.joiningDate);
  const months = getMonthsBetween(
    join.getFullYear(),
    join.getMonth(),
    uptoYear,
    uptoMonth,
  );

  const monthlySlips = months.map(({ year, month }) =>
    buildSalarySlip(employee, records, year, month),
  );

  const totalPaid = monthlySlips.reduce((sum, s) => sum + s.netPay, 0);
  const totalGross = monthlySlips.reduce((sum, s) => sum + s.grossSalary, 0);
  const totalDeduction = monthlySlips.reduce((sum, s) => sum + s.deduction, 0);

  return {
    joiningDate: employee.joiningDate,
    monthsCount: monthlySlips.length,
    monthlySlips,
    totalPaid,
    totalGross,
    totalDeduction,
  };
};

export const updateRecordStatus = (records, employeeId, date, status) => {
  const id = `${employeeId}_${date}`;
  const exists = records.some((r) => r.id === id);

  if (status === null) {
    return records.filter((r) => r.id !== id);
  }
  if (exists) {
    return records.map((r) => (r.id === id ? { ...r, status } : r));
  }
  return [...records, { id, employeeId, date, status }];
};

/** Update one employee's monthly gross salary, immutably. */
export const updateEmployeeSalary = (employees, employeeId, monthlySalary) =>
  employees.map((e) => (e.id === employeeId ? { ...e, monthlySalary } : e));

export const buildSalarySlip = (employee, records, year, month) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthPrefix = `${year}-${pad2(month + 1)}-`;

  const monthRecords = records.filter(
    (r) => r.employeeId === employee.id && r.date.startsWith(monthPrefix),
  );

  const counts = { Present: 0, Absent: 0, "Half-Day": 0, Leave: 0 };
  monthRecords.forEach((r) => {
    if (counts[r.status] !== undefined) counts[r.status] += 1;
  });

  const markedDays = monthRecords.length;
  const unmarkedDays = Math.max(daysInMonth - markedDays, 0);

  const dailyRate = employee.monthlySalary / daysInMonth;
  // Half-day pays half, Leave/Absent/unmarked pay nothing.
  const payableDays = counts.Present + counts["Half-Day"] * 0.5;
  const deduction = employee.monthlySalary - payableDays * dailyRate;
  const netPay = Math.max(employee.monthlySalary - deduction, 0);

  return {
    employee,
    year,
    month,
    daysInMonth,
    counts,
    markedDays,
    unmarkedDays,
    dailyRate,
    payableDays,
    grossSalary: employee.monthlySalary,
    deduction: Math.round(deduction),
    netPay: Math.round(netPay),
    records: monthRecords,
  };
};

// ─── Payment tracking (Paid / Payable / Prev Pending) ───────────────────────
// Used by the Salary Slip screen's status cards + per-row Payment toggle.

export const getPreviousMonth = (year, month) => {
  let m = month - 1;
  let y = year;
  if (m < 0) {
    m = 11;
    y -= 1;
  }
  return { year: y, month: m };
};

/** Storage key for whether one employee's salary was marked paid for one month. */
export const paidKey = (employeeId, year, month) =>
  `${employeeId}_${year}-${month}`;

/**
 * Sum of unpaid net pay from every month strictly before the selected
 * month, back to the employee's joining date. `paidSet` is a Set of
 * paidKey(...) strings for months already marked paid.
 */
export const buildPendingSummary = (
  employee,
  records,
  paidSet,
  year,
  month,
) => {
  const join = new Date(employee.joiningDate);
  const joinYear = join.getFullYear();
  const joinMonth = join.getMonth();
  const { year: prevYear, month: prevMonth } = getPreviousMonth(year, month);

  if (prevYear < joinYear || (prevYear === joinYear && prevMonth < joinMonth)) {
    return { pendingAmount: 0, pendingMonths: [] };
  }

  const months = getMonthsBetween(joinYear, joinMonth, prevYear, prevMonth);
  let pendingAmount = 0;
  const pendingMonths = [];

  months.forEach(({ year: y, month: m }) => {
    if (!paidSet.has(paidKey(employee.id, y, m))) {
      const slip = buildSalarySlip(employee, records, y, m);
      pendingAmount += slip.netPay;
      pendingMonths.push({ year: y, month: m, amount: slip.netPay });
    }
  });

  return { pendingAmount, pendingMonths };
};

// ─── Staff management ────────────────────────────────────────────────────
// Used by Staff.jsx + StaffFormModal.jsx. Kept separate from EMPLOYEES
// (attendance/payroll) since this models the fuller staff-onboarding record
// shown in the Add/Edit Staff form (CTC, designation, PF/ESI applicability).

export const STAFF_STATUS_OPTIONS = ["Active", "Inactive"];

export const INITIAL_STAFF = [
  {
    id: 1,
    staffId: "STF-001",
    name: "Arun Kumar",
    mobile: "9876543210",
    department: "Production",
    designation: "Operator",
    joiningDate: "2025-04-12",
    ctc: 342000,
    monthlyGross: 28500,
    basicSalary: 11400,
    monthlySalary: 28000,
    pfApplicable: true,
    esiApplicable: true,
    status: "Active",
  },
  {
    id: 2,
    staffId: "STF-002",
    name: "Meera Shah",
    mobile: "9876543211",
    department: "Accounts",
    designation: "Accountant",
    joiningDate: "2024-11-02",
    ctc: 384000,
    monthlyGross: 32000,
    basicSalary: 12800,
    monthlySalary: 32000,
    pfApplicable: true,
    esiApplicable: false,
    status: "Active",
  },
  {
    id: 3,
    staffId: "STF-003",
    name: "Rafiq Khan",
    mobile: "9876543212",
    department: "Packing",
    designation: "Helper",
    joiningDate: "2023-08-18",
    ctc: 312000,
    monthlyGross: 26000,
    basicSalary: 10400,
    monthlySalary: 26000,
    pfApplicable: false,
    esiApplicable: true,
    status: "Inactive",
  },
];

// Default PF/ESI rates — mirror whatever's set in HrmSettings' PF/ESI card.
// Wire this up to that same state/store once settings are persisted.
export const PF_ESI_RATES = {
  pfEmployeePercent: 8,
  pfEmployerPercent: 4,
  esiEmployeePercent: 0.75,
  esiEmployerPercent: 3.25,
};

/** Auto-calculated PF/ESI contribution amounts for one staff record. */
export const calcPfEsi = (staff, rates = PF_ESI_RATES) => {
  const base = Number(staff.monthlySalary) || 0;

  const pfEmployee = staff.pfApplicable
    ? Math.round((base * rates.pfEmployeePercent) / 100)
    : 0;
  const pfEmployer = staff.pfApplicable
    ? Math.round((base * rates.pfEmployerPercent) / 100)
    : 0;
  const esiEmployee = staff.esiApplicable
    ? Math.round((base * rates.esiEmployeePercent) / 100)
    : 0;
  const esiEmployer = staff.esiApplicable
    ? Math.round((base * rates.esiEmployerPercent) / 100)
    : 0;

  return { pfEmployee, pfEmployer, esiEmployee, esiEmployer };
};

/** Next sequential "STF-004" style ID based on the current staff list. */
export const nextStaffId = (staffList) => {
  const nums = staffList.map(
    (s) => parseInt(String(s.staffId).replace(/\D/g, ""), 10) || 0,
  );
  const max = nums.length ? Math.max(...nums) : 0;
  return `STF-${String(max + 1).padStart(3, "0")}`;
};

export const AllDesignation = [
  {
    _id: "626a324c0c4796c4b825e9fc",
    name: "Production",
  },
  {
    _id: "6464b34471db8e8d50cb11b2",
    name: "Accounts",
  },
  {
    _id: "66ec0c8fe20b7ac35286e5ba",
    name: "HR",
  },
  {
    _id: "67b0756f82a2796d79d8b285",
    name: "Store",
  },
  {
    _id: "67b078e930867e655fedbbaa",
    name: "Sales",
  },
  {
    _id: "67c952ecda088cb6021d9d8d",
    name: "Dispatch",
  },
];

export const ProductionSaleOrderData = {
  status: "success",
  PaymentPending: 2,
  page: 1,
  pages: 1,
  orders: [
    {
      status: "All",
      count: 0,
      prevCount: 106,
      amount: 1588,
    },
    {
      status: "New Order",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
    {
      status: "Cancelled",
      count: 0,
      prevCount: 11,
      amount: 0,
    },
    {
      status: "In Progress",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
    {
      status: "Payment Pending",
      count: 2,
      prevCount: 75,
      amount: 1588,
    },
    {
      status: "Payment Under Verification",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
    {
      status: "Packing In Progress",
      count: 0,
      prevCount: 20,
      amount: 0,
    },
    {
      status: "Dispatch Pending",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
    {
      status: "Dispatched",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
    {
      status: "Delivery Pending",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
    {
      status: "Completed",
      count: 0,
      prevCount: 0,
      amount: 0,
    },
  ],
  data: [
    {
      dealerId: 9151637187,
      orderId: 106,
      storeName: "Wallicon",
      city: "Lucknow",
      managerId: 10,
      ordermanager: "Ajay Sahu",
      managerContact: "1234567890",
      orderType: "Production & Sale",
      orderDate: "2026-07-16T10:23:26.090Z",
      subtotal: 300,
      totalShippingCharge: 50,
      totalAmount: 350,
      status: "Payment Pending",
      paymentStatus: "Payment Pending",
      createdBy: "RRA Admin",
      createdByID: 2,
      createdAt: "2026-07-16T10:23:26.093Z",
      lastAction: "2026-07-16T10:23:26.093Z",
      shippingAddress: {
        name: "Asmit",
        mobile: 9151637187,
        pincode: 226001,
        street: "Burlington",
        city: "Lucknow",
        district: "LUCKNOW",
        state: "UTTAR PRADESH",
        country: "India",
      },
      shippingType: "Combined",
      shippingInfo: [
        {
          shippingCharge: 50,
          courierName: "Lucknow City Delivery",
          methodName: "E-rickshaw",
          partnerName: "E-rickshaw",
          delInstruction: "",
          warehouseId: "GOGNJ01",
          warehousename: "Golaganj",
          isDispatchWarehouse: true,
        },
      ],
      items: [
        {
          itemNo: "HR-B1",
          warehouseId: "GOGNJ01",
          warehouse: "Golaganj",
          qty: 2,
          price: 150,
          unit: "Sq Feet",
          product_name: "PVC PANEL",
          thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
        },
      ],
    },
    {
      dealerId: 9151637187,
      orderId: 105,
      storeName: "Wallicon",
      city: "Lucknow",
      managerId: 10,
      ordermanager: "Ajay Sahu",
      managerContact: "1234567890",
      orderType: "Production & Sale",
      orderDate: "2026-07-16T10:22:56.013Z",
      subtotal: 1198,
      totalShippingCharge: 40,
      totalAmount: 1238,
      status: "Payment Pending",
      paymentStatus: "Payment Pending",
      createdBy: "RRA Admin",
      createdByID: 2,
      createdAt: "2026-07-16T10:22:56.019Z",
      lastAction: "2026-07-16T10:22:56.019Z",
      shippingAddress: {
        name: "Asmit",
        mobile: 9151637187,
        pincode: 226001,
        street: "Burlington",
        city: "Lucknow",
        district: "LUCKNOW",
        state: "UTTAR PRADESH",
        country: "India",
      },
      shippingType: "Combined",
      shippingInfo: [
        {
          shippingCharge: 40,
          courierName: "Lucknow City Delivery",
          methodName: "Rapido & Porter",
          partnerName: "Rapido & Porter",
          delInstruction: "",
          warehouseId: "BLTON01",
          warehousename: "Burlington",
          isDispatchWarehouse: true,
        },
      ],
      items: [
        {
          itemNo: "3D30001",
          warehouseId: "BLTON01",
          warehouse: "Burlington",
          qty: 2,
          price: 599,
          unit: "Roll",
          product_name: "WALLPAPERS",
          thumbnail: "content/items/1642152969765_THUMNAIL3D-30001.jpg",
        },
      ],
    },
  ],
  managers: [
    {
      _id: "626a324c0c4796c4b825e9fc",
      name: "Ajay Sahu",
      managerId: 10,
    },
    {
      _id: "6464b34471db8e8d50cb11b2",
      name: "Om 1",
      managerId: 18,
    },
    {
      _id: "66ec0c8fe20b7ac35286e5ba",
      name: "Mohit Jaiswal",
      managerId: 20,
    },
    {
      _id: "67b0756f82a2796d79d8b285",
      name: "Manish Ojha(OM)",
      managerId: 36,
    },
    {
      _id: "67b078e930867e655fedbbaa",
      name: "Satyam Sharma",
      managerId: 37,
    },
    {
      _id: "67c952ecda088cb6021d9d8d",
      name: "TestingManager",
      managerId: 38,
    },
    {
      _id: "6a0aee21e6d310ffb8369142",
      name: "Demo Manager",
      managerId: 50,
    },
  ],
  warehouses: [
    {
      _id: "6486b50d9df21b69a5c1180b",
      warehousename: "Wallpaper House Nishatganj",
      warehouseId: "WHNGJ01",
    },
    {
      _id: "624ad15376fad4a7da6cfc8d",
      warehousename: "Burlington",
      warehouseId: "BLTON01",
    },
    {
      _id: "624acea876fad4a7da6cf1c0",
      warehousename: "Golaganj",
      warehouseId: "GOGNJ01",
    },
    {
      _id: "624acf0d76fad4a7da6cf1d8",
      warehousename: "Nadarganj",
      warehouseId: "NDGNJ01",
    },
    {
      _id: "692841ba0963ff6fa20e98e1",
      warehousename: "Maharajganj",
      warehouseId: "MAHAZ01",
    },
    {
      _id: "6981c0801ea6594bfad79fd6",
      warehousename: "Alambagh",
      warehouseId: "ALAMB0001",
    },
    {
      _id: "6a0178ebc321dec7979be876",
      warehousename: "Ranchi Warehouse",
      warehouseId: "RANCH0001",
    },
    {
      _id: "6a0179d5c321dec7979be88f",
      warehousename: "West Warehouse",
      warehouseId: "WESTW0001",
    },
  ],
};

export const VesionsList = [
  "Version 1",
  "Version V2",
  "Version V3",
  "Version V4",
  "Version V5",
];

// inside each order object in `data`, alongside `items`
// export const versions = {
//   "Version 1": [
//     {
//       itemNo: "MCH003",
//       warehouseId: "GOGNJ01",
//       warehouse: "Golaganj",
//       qty: 2,
//       price: 150,
//       unit: "Sq Feet",
//       product_name: "PVC PANEL",
//       thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
//     },
//   ],
//   "Version 2": [
//     {
//       itemNo: "3D30005",
//       warehouseId: "GOGNJ01",
//       warehouse: "Golaganj",
//       qty: 3,
//       price: 145,
//       unit: "Sq Feet",
//       product_name: "PVC PANEL",
//       thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
//     },
//   ],
//   "Version 3": [
//     {
//       itemNo: "CE-H1",
//       warehouseId: "GOGNJ01",
//       warehouse: "Golaganj",
//       qty: 4,
//       price: 140,
//       unit: "Sq Feet",
//       product_name: "PVC PANEL",
//       thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
//     },
//   ],
//   "Version 4": [
//     {
//       itemNo: "CE-B1",
//       warehouseId: "GOGNJ01",
//       warehouse: "Golaganj",
//       qty: 2,
//       price: 155,
//       unit: "Sq Feet",
//       product_name: "PVC PANEL",
//       thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
//     },
//   ],
//   "Version 5": [
//     {
//       itemNo: "3D30001",
//       warehouseId: "GOGNJ01",
//       warehouse: "Golaganj",
//       qty: 1,
//       price: 160,
//       unit: "Sq Feet",
//       product_name: "PVC PANEL",
//       thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
//     },
//   ],
// };

export const versions = {
  "Version 1": {
    changedBy: "Rahul Sharma",
    designation: "Sales Executive",
    items: [
      {
        itemNo: "MCH003",
        warehouseId: "GOGNJ01",
        warehouse: "Golaganj",
        qty: 2,
        price: 150,
        unit: "Sq Feet",
        product_name: "PVC PANEL",
        thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
      },
    ],
  },

  "Version 2": {
    changedBy: "Amit Verma",
    designation: "Project Manager",
    items: [
      {
        itemNo: "3D30005",
        warehouseId: "GOGNJ01",
        warehouse: "Golaganj",
        qty: 3,
        price: 145,
        unit: "Sq Feet",
        product_name: "PVC PANEL",
        thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
      },
    ],
  },

  "Version 3": {
    changedBy: "Priya Singh",
    designation: "Interior Designer",
    items: [
      {
        itemNo: "CE-H1",
        warehouseId: "GOGNJ01",
        warehouse: "Golaganj",
        qty: 4,
        price: 140,
        unit: "Sq Feet",
        product_name: "PVC PANEL",
        thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
      },
    ],
  },

  "Version 4": {
    changedBy: "Neha Gupta",
    designation: "Senior Designer",
    items: [
      {
        itemNo: "CE-B1",
        warehouseId: "GOGNJ01",
        warehouse: "Golaganj",
        qty: 2,
        price: 155,
        unit: "Sq Feet",
        product_name: "PVC PANEL",
        thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
      },
    ],
  },

  "Version 5": {
    changedBy: "Vikas Mishra",
    designation: "Site Engineer",
    items: [
      {
        itemNo: "3D30001",
        warehouseId: "GOGNJ01",
        warehouse: "Golaganj",
        qty: 1,
        price: 160,
        unit: "Sq Feet",
        product_name: "PVC PANEL",
        thumbnail: "content/items/1642414241150_THUMNAILIMAGEBedRoom_46.jpg",
      },
    ],
  },
};
